import React, { useState, useEffect } from 'react';
import { Brain, Sparkles, Loader2, AlertCircle, Play } from 'lucide-react';
import aiService from '../services/aiService';
import youtubeService from '../services/youtubeService';
import { t } from '../utils/translations';

const FALLBACK_THUMB = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=320&h=180&fit=crop&crop=center';

const AIRecommendation = ({ language, playlistHistory = [], currentSong = null }) => {
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [youtubeRecommendations, setYoutubeRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [copiedKeyword, setCopiedKeyword] = useState(null);

  // aiService 연결은 1회
  useEffect(() => {
    youtubeService.setAIService(aiService);
  }, []);



  // 고급 분석 (플레이리스트 내용 포함)
  const runAdvancedAIAnalysis = async () => {
    if (!aiService.isConfigured()) {
      setError(t('aiRecommendation.errors.noApiKey', language));
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      // 고급 분석 (플레이리스트 내용 포함)
      const analysisResult = await aiService.analyzeMusicTasteWithContent(playlistHistory, {}, language);

      if (analysisResult.success) {
        setAiAnalysis({ ...analysisResult, lastLanguage: language, isAdvanced: true });
        
        // AI 분석이 성공하면 분석 결과 기반으로 플레이리스트 로드
        const aiBasedPlaylists = await youtubeService.getRecommendationsFromAIAnalysis(analysisResult, 5, language);
        if (aiBasedPlaylists.success && aiBasedPlaylists.playlists.length > 0) {
          setYoutubeRecommendations(aiBasedPlaylists.playlists);
        } else {
          // AI 기반 추천이 실패하면 기본 추천 시도
          const fallbackRecommendations = await youtubeService.getPopularPlaylists(5, language);
          if (fallbackRecommendations.success && fallbackRecommendations.playlists.length > 0) {
            setYoutubeRecommendations(fallbackRecommendations.playlists);
          }
        }
      } else {
        setError(analysisResult.error);
      }
    } catch {
      setError(t('aiRecommendation.errors.analysisFailed', language));
    } finally {
      setIsLoading(false);
    }
  };

  // 새로운 추천 받기
  const getNewRecommendations = async () => {
    if (!aiService.isConfigured()) {
      setError(t('aiRecommendation.errors.noApiKey', language));
      return;
    }

    // 플레이리스트만 업데이트 (분석 결과는 그대로 유지)
    setError(null);
    
    try {
      const newRecommendations = await youtubeService.getRecommendationsFromAIAnalysis(aiAnalysis, 5, language, true);
      if (newRecommendations.success && newRecommendations.playlists.length > 0) {
        setYoutubeRecommendations(newRecommendations.playlists);
      } else {
        // AI 기반 추천이 실패하면 기본 추천 시도
        const fallbackRecommendations = await youtubeService.getPopularPlaylists(5, language, true);
        if (fallbackRecommendations.success && fallbackRecommendations.playlists.length > 0) {
          setYoutubeRecommendations(fallbackRecommendations.playlists);
        } else {
          setError(t('aiRecommendation.errors.noRecommendations', language));
        }
      }
    } catch {
      setError(t('aiRecommendation.errors.recommendationFailed', language));
    }
  };



  // 외부 호출용(버튼) - 현재 사용하지 않음
  // const loadYouTubeRecommendations = async () => {
  //   setIsLoading(true);
  //   setError(null);
  //   try {
  //     let result;
  //     if (currentSong?.title) {
  //       result = await youtubeService.getRealTimeRecommendations(currentSong.title, 3, language);
  //     } else if (playlistHistory.length > 0) {
  //       result = await youtubeService.getRecommendationsFromHistory(playlistHistory, 5, language);
  //     } else {
  //       result = await youtubeService.getPopularPlaylists(10, language);
  //     }
  //     if (result.success) {
  //       setYoutubeRecommendations(result.playlists);
  //     } else {
  //       setError(result.error);
  //     }
  //   } catch {
  //     setError('AI 추천을 가져오는 중 오류가 발생했습니다.');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // 언어가 바뀌면 재분석 (기존 언어와 다를 때만)
  useEffect(() => {
    if (aiAnalysis && aiAnalysis.lastLanguage !== language) {
      runAdvancedAIAnalysis();
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlaylist = (playlist) => {
    const title = playlist?.title || '';
    const searchQuery = encodeURIComponent(`${title} playlist`);
    const url = `https://www.youtube.com/results?search_query=${searchQuery}&sp=EgIQAw%3D%3D`;
    window.open(url, '_blank');
  };

  const copyKeyword = async (keyword) => {
    try {
      await navigator.clipboard.writeText(keyword);
      setCopiedKeyword(keyword);
      setTimeout(() => setCopiedKeyword(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = keyword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedKeyword(keyword);
      setTimeout(() => setCopiedKeyword(null), 2000);
    }
  };

  return (
    <div className="ai-recommendation-section">
      {/* 헤더와 분석 시작 버튼 */}
             <div className="section-header">
         <h2 className="section-title">
           <Sparkles size={24} />
           {t('aiRecommendation.title', language)}
         </h2>
         <button
           onClick={runAdvancedAIAnalysis}
           disabled={isLoading}
           className="analyze-btn"
           title={t('aiRecommendation.advancedAnalysisTooltip', language)}
         >
           {isLoading ? (
             <>
               <Loader2 size={16} className="spinner" />
               {t('aiRecommendation.analyzing', language)}
             </>
           ) : (
             <>
               <Brain size={16} />
               {t('aiRecommendation.startAnalysis', language)}
             </>
           )}
         </button>
       </div>



      {/* 로딩 상태 */}
      {isLoading && (
        <div className="loading-state">
          <Loader2 size={24} className="spinner" />
          <p>{t('aiRecommendation.loading', language)}</p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && (
        <div className="error-state">
          <AlertCircle size={24} />
          <p>{error}</p>
        </div>
      )}

      {/* AI 분석 결과 */}
      {aiAnalysis && !isLoading && (
        <div className="ai-analysis-result">
          <div className="analysis-content">
            <h3>{t('aiRecommendation.analysis.title', language)}</h3>

            {playlistHistory.length === 0 && (
              <div className="info-message">
                <p>
                  {t('aiRecommendation.analysis.noHistory', language)}
                </p>
              </div>
            )}

            <div className="analysis-summary">
              {(aiAnalysis.recommendations || aiAnalysis.analysis) && (
                <>
                  <div className="analysis-item">
                                         <h4>
                       {t('aiRecommendation.analysis.tags.genres', language)}
                     </h4>
                    <div className="tag-list">
                      {/* 장르 */}
                      {Array.isArray(aiAnalysis?.recommendations?.preferredGenres) &&
                        aiAnalysis.recommendations.preferredGenres.map((genre, i) => (
                          <span
                            key={`genre-${i}-${genre}`}
                            className={`analysis-tag genre-tag ${copiedKeyword === genre ? 'copied' : ''}`}
                            onClick={() => copyKeyword(genre)}
                            title={t('common.clickToCopy', language)}
                          >
                            {copiedKeyword === genre ? `✓ ${t('common.copied', language)}` : genre}
                          </span>
                        ))}

                      {/* 무드 */}
                      {Array.isArray(aiAnalysis?.recommendations?.preferredMood) &&
                        aiAnalysis.recommendations.preferredMood.map((mood, i) => (
                          <span
                            key={`mood-${i}-${mood}`}
                            className={`analysis-tag mood-tag ${copiedKeyword === mood ? 'copied' : ''}`}
                            onClick={() => copyKeyword(mood)}
                            title={t('common.clickToCopy', language)}
                          >
                            {copiedKeyword === mood ? `✓ ${t('common.copied', language)}` : mood}
                          </span>
                        ))}

                      {/* 키워드 */}
                      {Array.isArray(aiAnalysis?.recommendations?.recommendedKeywords) &&
                        aiAnalysis.recommendations.recommendedKeywords.map((kw, i) => (
                          <span
                            key={`kw-${i}-${kw}`}
                            className={`analysis-tag keyword-tag ${copiedKeyword === kw ? 'copied' : ''}`}
                            onClick={() => copyKeyword(kw)}
                            title={t('common.clickToCopy', language)}
                          >
                            {copiedKeyword === kw ? `✓ ${t('common.copied', language)}` : kw}
                          </span>
                        ))}
                    </div>
                  </div>

                  {aiAnalysis?.recommendations?.reasoning && (
                    <div className="analysis-item">
                      <h4>{t('aiRecommendation.analysis.tags.insight', language)}</h4>
                      <p className="reasoning-text">{aiAnalysis.recommendations.reasoning}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI 플레이리스트 추천 결과 */}
      {youtubeRecommendations.length > 0 && !isLoading && (
        <div className="youtube-recommendations">
          <h3>
            {currentSong
              ? t('aiRecommendation.recommendations.currentSong', language)
              : playlistHistory.length > 0
              ? t('aiRecommendation.recommendations.aiGenerated', language)
              : t('aiRecommendation.recommendations.popular', language)}
          </h3>

          <div className="playlist-grid">
            {youtubeRecommendations.map((playlist, idx) => (
              <div
                key={playlist.id || `${playlist.title}-${playlist.channelTitle || ''}-${idx}`}
                className="playlist-card"
                onClick={() => loadPlaylist(playlist)}
              >
                <div className="playlist-thumbnail">
                  <img
                    src={playlist.thumbnail || FALLBACK_THUMB}
                    alt={playlist.title}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_THUMB;
                    }}
                  />
                  <div className="playlist-overlay">
                    <Play size={24} />
                  </div>
                </div>
                <div className="playlist-info">
                  <h4 className="playlist-title">{playlist.title}</h4>
                  {playlist.channelTitle && (
                    <p className="playlist-channel">{playlist.channelTitle}</p>
                  )}
                  {typeof playlist.videoCount === 'number' && playlist.videoCount > 0 && (
                    <p className="playlist-count">
                      {`${playlist.videoCount}${t('aiRecommendation.recommendations.songCount', language)}`}
                    </p>
                  )}
                  {Array.isArray(playlist.keywords) && playlist.keywords.length > 0 && (
                    <div className="playlist-keywords">
                      {playlist.keywords.slice(0, 3).map((keyword, i) => (
                        <span key={i} className="playlist-keyword">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

             {aiAnalysis && (
         <div className="refresh-section">
           <button className="refresh-btn" onClick={getNewRecommendations} disabled={isLoading}>
             <Sparkles size={16} />
             {language === 'ko' ? '새로운 추천 받기' : 'Get New Recommendations'}
           </button>
         </div>
       )}
    </div>
  );
};

export default AIRecommendation;